/**
 * Product Showcase Section JavaScript
 * Enhanced functionality for portrait neo cards with responsive behavior
 */

class ProductShowcase {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.grid = container.querySelector('.productshowcase__neo-grid');
    this.cards = container.querySelectorAll('.neo-card-wrapper');
    
    this.init();
  }

  init() {
    this.setupResponsiveGrid();
    this.setupScrollSnap();
    this.setupAccessibility();
    this.setupIntersectionObserver();
    
    // Listen for window resize
    window.addEventListener('resize', this.debounce(this.handleResize.bind(this), 250));
  }

  setupResponsiveGrid() {
    if (!this.grid) return;
    
    // Add data attribute for total card count to support CSS targeting
    this.grid.setAttribute('data-total-cards', this.cards.length);
    
    // Add responsive classes based on screen size
    this.updateGridLayout();
  }

  setupScrollSnap() {
    if (!this.grid) return;
    
    // DEBUG LOG: Track scroll setup
    console.log('[ProductShowcase Debug] Setting up scroll snap for width:', window.innerWidth);
    
    // DISABLED: Mobile scroll handling completely removed to allow normal vertical scroll
    // Mobile now uses regular grid layout, no horizontal scroll needed
    if (window.innerWidth <= 650) {
      console.log('[ProductShowcase Debug] Mobile detected - using grid layout, no scroll handling needed');
      return; // Exit early for mobile - no scroll setup needed
    }
    
    // Only apply scroll handling for tablet/desktop if needed
    if (window.innerWidth > 650 && window.innerWidth <= 900) {
      // Keep tablet scroll handling if horizontal overflow exists
      if (this.grid.scrollWidth > this.grid.clientWidth) {
        this.grid.addEventListener('scroll', this.debounce(this.handleScroll.bind(this), 200));
      }
    }
  }

  setupAccessibility() {
    // Add ARIA labels and keyboard navigation
    this.cards.forEach((card, index) => {
      const cardElement = card.querySelector('.neo-card');
      if (cardElement) {
        cardElement.setAttribute('tabindex', '0');
        cardElement.setAttribute('aria-label', `Product ${index + 1} of ${this.cards.length}`);
        
        // Add keyboard support
        cardElement.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            cardElement.click();
          }
        });
      }
    });
  }

  setupIntersectionObserver() {
    // Animate cards on scroll into view
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.style.opacity = '1';
            entry.target.style.transform = 'translateY(0)';
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '50px'
      });

      this.cards.forEach(card => {
        // Set initial state
        card.style.opacity = '0';
        card.style.transform = 'translateY(20px)';
        card.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
        
        observer.observe(card);
      });
    }
  }

  updateGridLayout() {
    if (!this.grid) return;
    
    const width = window.innerWidth;
    
    // Remove existing responsive classes
    this.grid.classList.remove('grid--mobile', 'grid--tablet', 'grid--desktop');
    
    if (width <= 650) {
      this.grid.classList.add('grid--mobile');
      // DISABLED: No mobile scroll setup needed - using normal grid layout
    } else if (width <= 900) {
      this.grid.classList.add('grid--tablet');
      this.setupTabletScroll();
    } else {
      this.grid.classList.add('grid--desktop');
    }
  }

  setupMobileScroll() {
    // DISABLED: Mobile scroll setup completely removed
    // Mobile now uses normal grid layout without horizontal scroll
    return;
  }

  setupTabletScroll() {
    if (!this.grid) return;
    
    // Add scroll indicators for tablet - needed for problematic 650px-900px range
    const hasScrollIndicators = this.container.querySelector('.scroll-indicator');
    if (!hasScrollIndicators && this.shouldShowArrows()) {
      this.addScrollIndicators('tablet');
    }
  }

  shouldShowArrows() {
    // Show arrows based on card count and viewport to ensure all cards are accessible
    const totalCards = this.cards.length;
    const width = window.innerWidth;
    
    // DISABLED: No arrows for mobile since we removed horizontal scroll
    if (width <= 650) return false; // Mobile uses grid layout, no scroll arrows needed
    
    // Show for 8-card grids in tablet range to access cards 5-8
    if (totalCards === 8) {
      if (width > 651 && width <= 900) return true; // Tablet range
    }
    
    // Show for any horizontal overflow on non-mobile devices
    return this.grid && width > 650 && this.grid.scrollWidth > this.grid.clientWidth;
  }

  addScrollIndicators(mode = 'mobile') {
    // Create scroll indicators with better icons
    const leftIndicator = document.createElement('div');
    leftIndicator.className = `scroll-indicator scroll-indicator--left scroll-indicator--${mode}`;
    leftIndicator.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polyline points="15,18 9,12 15,6"></polyline>
    </svg>`;
    
    const rightIndicator = document.createElement('div');
    rightIndicator.className = `scroll-indicator scroll-indicator--right scroll-indicator--${mode}`;
    rightIndicator.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
      <polyline points="9,18 15,12 9,6"></polyline>
    </svg>`;
    
    this.container.style.position = 'relative';
    this.container.appendChild(leftIndicator);
    this.container.appendChild(rightIndicator);
    
    // Add enhanced scroll indicator styles
    const style = document.createElement('style');
    style.textContent = `
      .scroll-indicator {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        background: rgba(114, 47, 55, 0.9);
        color: white;
        width: 44px;
        height: 44px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        z-index: 10;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        opacity: 0;
        border: 2px solid rgba(255, 255, 255, 0.2);
        backdrop-filter: blur(8px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }
      
      .scroll-indicator svg {
        width: 20px;
        height: 20px;
        stroke-width: 2.5;
      }
      
      .scroll-indicator:hover {
        background: rgba(114, 47, 55, 1);
        transform: translateY(-50%) scale(1.08);
        box-shadow: 0 6px 20px rgba(114, 47, 55, 0.4);
        border-color: rgba(255, 255, 255, 0.3);
      }
      
      .scroll-indicator--left {
        left: 15px;
      }
      
      .scroll-indicator--right {
        right: 15px;
      }
      
      /* Show arrows on hover for desktop/tablet, always visible on mobile */
      .productshowcase:hover .scroll-indicator {
        opacity: 1;
      }
      
      /* Mobile specific styles */
      @media (max-width: 650px) {
        .scroll-indicator--mobile {
          opacity: 0.85;
          background: rgba(114, 47, 55, 0.95);
          width: 40px;
          height: 40px;
        }
        
        .scroll-indicator--mobile:hover {
          transform: translateY(-50%) scale(1.05);
        }
      }
      
      /* Tablet specific styles - more prominent for the problematic range */
      @media (min-width: 651px) and (max-width: 900px) {
        .scroll-indicator--tablet {
          opacity: 0.7;
          background: rgba(114, 47, 55, 0.92);
          width: 46px;
          height: 46px;
        }
        
        .scroll-indicator--tablet svg {
          width: 22px;
          height: 22px;
        }
        
        .productshowcase:hover .scroll-indicator--tablet {
          opacity: 1;
        }
      }
    `;
    
    if (!document.head.querySelector('style[data-scroll-indicators]')) {
      style.setAttribute('data-scroll-indicators', 'true');
      document.head.appendChild(style);
    }
    
    // Add click handlers with dynamic scroll distance
    const getScrollDistance = () => {
      const width = window.innerWidth;
      if (width <= 650) return 280; // Mobile card width + gap
      if (width <= 900) return 320; // Tablet card width + gap
      return 300; // Desktop fallback
    };
    
    leftIndicator.addEventListener('click', () => {
      this.grid.scrollBy({ left: -getScrollDistance(), behavior: 'smooth' });
    });
    
    rightIndicator.addEventListener('click', () => {
      this.grid.scrollBy({ left: getScrollDistance(), behavior: 'smooth' });
    });
    
    // Update indicators on scroll
    this.grid.addEventListener('scroll', () => {
      leftIndicator.style.opacity = this.grid.scrollLeft > 0 ? '0.8' : '0.4';
      rightIndicator.style.opacity =
        this.grid.scrollLeft < (this.grid.scrollWidth - this.grid.clientWidth) ? '0.8' : '0.4';
    });
  }

  handleScroll() {
    // DEBUG LOG: Track scroll events and potential conflicts
    console.log('[ProductShowcase Debug] Handling scroll:', {
      scrollLeft: this.grid.scrollLeft,
      windowWidth: window.innerWidth,
      scrollWidth: this.grid.scrollWidth,
      clientWidth: this.grid.clientWidth
    });
    
    // DISABLED: No scroll handling on mobile since we removed horizontal scroll
    if (window.innerWidth <= 650) {
      console.log('[ProductShowcase Debug] Mobile detected - no scroll handling needed');
      return; // Exit early for mobile
    }
    
    // Only handle scroll for tablet/desktop with horizontal overflow
    if (this.grid && window.innerWidth > 650 && this.grid.scrollWidth > this.grid.clientWidth) {
      // Optional: Add scroll handling for tablet/desktop if needed
      // Currently just logging for debug purposes
    }
  }

  handleResize() {
    this.updateGridLayout();
  }

  // Utility function for debouncing
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Public method to refresh the showcase
  refresh() {
    this.cards = this.container.querySelectorAll('.neo-card-wrapper');
    
    // Update card count attribute
    if (this.grid) {
      this.grid.setAttribute('data-total-cards', this.cards.length);
    }
    
    this.setupAccessibility();
    this.updateGridLayout();
  }

  // Public method to destroy the showcase
  destroy() {
    window.removeEventListener('resize', this.handleResize);
    
    // Remove scroll indicators
    const indicators = this.container.querySelectorAll('.scroll-indicator');
    indicators.forEach(indicator => indicator.remove());
  }
}

// Initialize ProductShowcase on DOMContentLoaded
document.addEventListener('DOMContentLoaded', () => {
  const showcases = document.querySelectorAll('[data-section-id*="productshowcase"], [id*="new-products"]');
  
  showcases.forEach(showcase => {
    new ProductShowcase(showcase);
  });
});

// Initialize for theme editor
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.classList.contains('section-productshowcase') || event.target.id.includes('new-products')) {
      new ProductShowcase(event.target);
    }
  });
}

// Export for external use
window.ProductShowcase = ProductShowcase;